import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { Printer, ArrowLeft, User as UserIcon } from "lucide-react";

/**
 * Per-person workstation snapshot — one A4 page per staff member listing
 * every asset assigned to them (assignee_kind === "user"). Managers print
 * these before an asset walk-around and tick off each line as they visit
 * each desk; anything added/removed since gets circled by hand and typed
 * back into the Assets page afterwards.
 *
 * Query params:
 *   ?user=<user_id>   → single-person snapshot
 *   (nothing)          → all staff with ≥ 1 assigned asset
 */
const WorkstationPrint = () => {
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [people, setPeople] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  // v187 — accepts either ?user=<user_id> or ?person=<person_id>
  const focusUserId = params.get("user") || "";
  const focusPersonId = params.get("person") || "";
  // A single focus value that encodes kind: "user:<id>" or "person:<id>"
  const focusKey = focusUserId
    ? `user:${focusUserId}`
    : focusPersonId
      ? `person:${focusPersonId}`
      : "";

  useEffect(() => {
    (async () => {
      try {
        const [{ data: a }, { data: u }, { data: c }, { data: p }] = await Promise.all([
          api.get("/assets"),
          api.get("/users/public"),
          api.get("/asset-categories"),
          api.get("/people?include_inactive=true").catch(() => ({ data: [] })),
        ]);
        setAssets(a || []);
        setUsers(u || []);
        setCategories(c || []);
        setPeople(p || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // v187 — Group assets by BOTH login users and non-login People so
  // the Workstation snapshot walk-around covers everyone (Cameron, Faye
  // etc. don't need a PIN to have their desk audited).
  const byOwner = useMemo(() => {
    const byId = new Map();
    users.forEach((u) => byId.set(`user:${u.id}`, { kind: "user", owner: u, items: [] }));
    people.forEach((p) =>
      byId.set(`person:${p.id}`, { kind: "person", owner: p, items: [] }),
    );
    (assets || [])
      .filter(
        (a) =>
          (a.assignee_kind === "user" || a.assignee_kind === "person") &&
          a.assignee_id,
      )
      .filter((a) => {
        if (!focusKey) return true;
        return `${a.assignee_kind}:${a.assignee_id}` === focusKey;
      })
      .forEach((a) => {
        const k = `${a.assignee_kind}:${a.assignee_id}`;
        if (!byId.has(k)) {
          byId.set(k, {
            kind: a.assignee_kind,
            owner: { id: a.assignee_id, name: a.assignee_text || "Unknown" },
            items: [],
          });
        }
        byId.get(k).items.push(a);
      });
    return Array.from(byId.values())
      .filter((g) => g.items.length > 0)
      .sort((x, y) => (x.owner.name || "").localeCompare(y.owner.name || ""))
      .map((g) => ({
        ...g,
        items: [...g.items].sort((a, b) =>
          (a.category || "z").localeCompare(b.category || "z"),
        ),
      }));
  }, [assets, users, people, focusKey]);

  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  if (loading) {
    return <div className="p-10 text-center">Loading workstation data…</div>;
  }

  return (
    <div className="wsp-root">
      <style>{`
        .wsp-root { background: #ECEFF4; min-height: 100vh; }
        .wsp-toolbar {
          position: sticky; top: 0; z-index: 10;
          background: #fff; border-bottom: 2px solid #000;
          padding: 12px 20px; display: flex; align-items: center;
          gap: 12px; flex-wrap: wrap;
        }
        .wsp-page {
          background: white; margin: 20px auto; padding: 40px 42px;
          width: 210mm; min-height: 297mm; box-sizing: border-box;
          box-shadow: 0 2px 12px rgba(0,0,0,0.15);
          font-family: Inter, system-ui, sans-serif;
          color: #111;
          page-break-after: always;
        }
        .wsp-page:last-child { page-break-after: auto; }
        .wsp-eyebrow {
          font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase;
          color: #7A5300; font-weight: 700;
        }
        .wsp-h1 { font-size: 34px; font-weight: 800; line-height: 1.1; margin: 4px 0 2px; }
        .wsp-meta { font-size: 12px; color: #4a5665; margin-bottom: 20px; }
        .wsp-count {
          display: inline-block; background: #FFF8E1; border: 2px solid #7A5300;
          padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700;
          color: #3B2A00; margin-left: 6px;
        }
        table.wsp-tbl { width: 100%; border-collapse: collapse; font-size: 11px; }
        table.wsp-tbl th {
          text-align: left; padding: 6px 8px; border-bottom: 2px solid #000;
          font-size: 9px; letter-spacing: 1px; text-transform: uppercase;
          color: #444; background: #FAFBFC;
        }
        table.wsp-tbl td {
          padding: 8px; border-bottom: 1px solid #DDE1E6;
          vertical-align: top;
        }
        table.wsp-tbl tr:nth-child(even) td { background: #FAFBFC; }
        .wsp-thumb {
          width: 44px; height: 44px; object-fit: cover;
          border: 1px solid #CCC; border-radius: 4px; background: #F0F0F0;
        }
        .wsp-thumb-ph {
          width: 44px; height: 44px; border: 1px dashed #CCC;
          border-radius: 4px; background: #FAFAFA;
          display: flex; align-items: center; justify-content: center;
          font-size: 8px; color: #999;
        }
        .wsp-sig {
          margin-top: 30px; padding-top: 20px; border-top: 2px dashed #999;
          display: grid; grid-template-columns: 1fr 1fr; gap: 30px;
          font-size: 11px;
        }
        .wsp-sig-line {
          border-bottom: 1px solid #000; padding-bottom: 20px;
        }
        .wsp-sig-label { color: #607087; margin-top: 4px; }
        .wsp-tick {
          display: inline-block; width: 14px; height: 14px;
          border: 1.5px solid #000; border-radius: 2px; margin-right: 6px;
        }
        .wsp-empty {
          background: white; margin: 40px auto; padding: 50px;
          width: 210mm; text-align: center; border: 2px solid #000;
        }
        @media print {
          .wsp-toolbar { display: none !important; }
          .wsp-root { background: white; }
          .wsp-page { margin: 0; box-shadow: none; }
        }
      `}</style>

      <div className="wsp-toolbar" data-testid="workstation-print-toolbar">
        <button
          data-testid="wsp-back"
          onClick={() => nav("/assets")}
          className="nb-btn bg-white px-3 py-2 flex items-center gap-1.5 text-sm"
        >
          <ArrowLeft size={14} strokeWidth={2.5} /> Back to Assets
        </button>
        <div className="label-eyebrow">Workstation snapshots</div>
        <select
          data-testid="wsp-user-picker"
          className="nb-input py-1.5 text-sm"
          value={focusKey}
          onChange={(e) => {
            const v = e.target.value; // "" or "user:<id>" or "person:<id>"
            const next = new URLSearchParams(params);
            next.delete("user");
            next.delete("person");
            if (v.startsWith("user:")) next.set("user", v.slice(5));
            else if (v.startsWith("person:")) next.set("person", v.slice(7));
            setParams(next, { replace: true });
          }}
        >
          <option value="">All staff (one page per person)</option>
          {/* v187 — merged user + person picker. Sorted by name so the
              user can't remember whether Cameron is a login or a Person. */}
          {[
            ...users
              .filter((u) =>
                assets.some((a) => a.assignee_kind === "user" && a.assignee_id === u.id),
              )
              .map((u) => ({ key: `user:${u.id}`, name: u.name, kind: "user" })),
            ...people
              .filter((p) =>
                assets.some((a) => a.assignee_kind === "person" && a.assignee_id === p.id),
              )
              .map((p) => ({
                key: `person:${p.id}`,
                name: p.name + (p.role ? ` · ${p.role}` : ""),
                kind: "person",
              })),
          ]
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            .map((o) => (
              <option key={o.key} value={o.key}>
                {o.name}
                {o.kind === "person" ? " (no login)" : ""}
              </option>
            ))}
        </select>
        <div className="text-xs text-[#607087] ml-auto">
          {byOwner.length} staff · {byOwner.reduce((s, g) => s + g.items.length, 0)} assets
        </div>
        <button
          data-testid="wsp-print"
          onClick={() => window.print()}
          className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-1.5 text-sm"
        >
          <Printer size={14} strokeWidth={2.5} /> Print
        </button>
      </div>

      {byOwner.length === 0 ? (
        <div className="wsp-empty" data-testid="wsp-empty">
          <div className="wsp-eyebrow">Nothing to print</div>
          <div className="wsp-h1">No assigned assets</div>
          <div className="wsp-meta">
            {focusKey
              ? "That person doesn't have any assets assigned yet."
              : "Assign assets to staff (login users or People) on the Assets page before printing snapshots."}
          </div>
        </div>
      ) : (
        byOwner.map((g) => (
          <div
            key={`${g.kind}:${g.owner.id}`}
            className="wsp-page"
            data-testid={`wsp-page-${g.owner.id}`}
          >
            <div className="wsp-eyebrow">
              Footwork · Workstation snapshot
              {g.kind === "person" && " · non-login staff"}
            </div>
            <div className="wsp-h1">
              {g.owner.name}
              <span className="wsp-count">
                {g.items.length} asset{g.items.length === 1 ? "" : "s"}
              </span>
            </div>
            {/* v187 — Person context line: role · dept · desk. Only rendered
                for the Person kind since login users don't carry desk info. */}
            {g.kind === "person" && (g.owner.role || g.owner.department || g.owner.desk) && (
              <div style={{ fontSize: 12, color: "#3D4A5C", margin: "-2px 0 6px", fontWeight: 600 }}>
                {[g.owner.role, g.owner.department, g.owner.desk && `🪑 ${g.owner.desk}`]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            )}
            <div className="wsp-meta">
              {today} · Walk-around check-sheet · Tick each item you see, circle
              anything different, and hand back for update.
            </div>

            <table className="wsp-tbl">
              <thead>
                <tr>
                  <th style={{ width: 22 }}></th>
                  <th style={{ width: 52 }}>Photo</th>
                  <th>Name / Category</th>
                  <th>Brand · Model</th>
                  <th>Serial</th>
                  <th>Condition</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((a) => {
                  const photo = (a.photos && a.photos[0]) || null;
                  const url = photo
                    ? imageUrl(photo.thumbnail_url || photo.url)
                    : null;
                  return (
                    <tr key={a.id} data-testid={`wsp-row-${a.id}`}>
                      <td>
                        <span className="wsp-tick" />
                      </td>
                      <td>
                        {url ? (
                          <img
                            src={url}
                            alt=""
                            className="wsp-thumb"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div className="wsp-thumb-ph">no photo</div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 700 }}>{a.name}</div>
                        <div style={{ fontSize: 10, color: "#607087" }}>
                          {a.category || "—"}
                        </div>
                      </td>
                      <td>
                        <div>{a.manufacturer || a.brand || "—"}</div>
                        <div style={{ fontSize: 10, color: "#607087" }}>
                          {a.model || ""}
                        </div>
                      </td>
                      <td style={{ fontFamily: "ui-monospace, monospace", fontSize: 10 }}>
                        {a.serial || "—"}
                      </td>
                      <td>{a.condition || "—"}</td>
                      <td>{a.location || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="wsp-sig">
              <div>
                <div className="wsp-sig-line" />
                <div className="wsp-sig-label">Staff signature</div>
              </div>
              <div>
                <div className="wsp-sig-line" />
                <div className="wsp-sig-label">Auditor · date</div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default WorkstationPrint;
